import { useRouter } from "next/router";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useInView } from "react-intersection-observer";
import "resize-observer-polyfill";

import useStorefrontCollection from "../../hooks/useStorefrontCollection";
import useStorefrontCollections from "../../hooks/useStorefrontCollections";
import useStorefrontProducts from "../../hooks/useStorefrontProducts";
import { collectionPath, findCollectionByHandle } from "../../lib/collectionUrls";
import {
  activeFilterCount,
  filtersFromQuery,
  filtersToQuery,
  withoutFilters,
} from "../../lib/listingFilters";
import useCart from "../../store/cart-store";
import Basket from "../Cart/Basket";
// Listing filter hidden for now.
// import ListingFilter from "../ListingFilter";
import NavBar from "../Navbar";
import ShopStatusPage, { statusCopyClassName } from "../ShopStatusPage";
import SideNavDrawer from "../SideNavDrawer";
import { Skeleton } from "../Skeleton";
import SkeletonCard from "../SkeletonCard";
import StoreCollections from "../StoreCollections";
import StoreItem from "../StoreItem";
import RestorableList from "./RestorableList";

export const ALL_PRODUCTS_OBJECT = {
  title: "all products",
  _id: "all",
  id: "all",
};

const pathForCollection = (collection) => {
  if (!collection || collection._id === "all" || collection.title === "all products") {
    return "/collections/all";
  }

  return collectionPath(collection);
};

const CollectionHome = ({ store, theme, hasBanner }) => {
  const router = useRouter();

  // The URL segment: a collection handle, or "all". Next has already decoded it.
  const routeHandle = useMemo(() => {
    if (router.pathname === "/collections/all") return "all";

    if (router.pathname === "/collections/[name]") {
      if (typeof router.query.name !== "string") return null; // wait for router
      // "all" in any case is the all-products listing, as it was before handles.
      return router.query.name.toLowerCase() === "all" ? "all" : router.query.name;
    }

    // Homepage listing (and any non-collections host of this component) = all products
    return "all";
  }, [router.pathname, router.query.name]);

  const isAllRoute = routeHandle === "all";
  const [lastEl, setLastEl] = useState(null);
  const { ref, inView } = useInView({
    threshold: 0,
  });

  const {
    data: collectionsData,
    isLoading: isCollectionsLoading,
    error: collectionsError,
  } = useStorefrontCollections();

  const collections = useMemo(() => collectionsData?.collections || [], [collectionsData]);

  // A URL that is a listed collection's handle is already resolved (chip taps, nav links).
  const listedCollection = useMemo(
    () => (routeHandle && !isAllRoute ? findCollectionByHandle(collections, routeHandle) : null),
    [routeHandle, isAllRoute, collections],
  );

  // Anything else (beyond the capped list, or an old URL) is resolved by the backend, which
  // answers old URLs with a redirect. Asked only once the list has loaded without a match.
  const needsLookup =
    Boolean(routeHandle) && !isAllRoute && !isCollectionsLoading && !listedCollection;
  const lookup = useStorefrontCollection(needsLookup ? routeHandle : null);
  const collectionLookup = needsLookup ? lookup.data : null;
  const collectionLookupError = needsLookup ? lookup.error : null;

  // Null until the route resolves: nothing is fetched, so a deep link never flashes the
  // all-products listing (or the previous collection's products) first.
  const selectedCollection = useMemo(() => {
    if (routeHandle === null) return null;
    if (isAllRoute) return ALL_PRODUCTS_OBJECT;
    if (listedCollection) return listedCollection;
    const resolved = collectionLookup?.collection;
    return resolved ? { ...resolved, _id: resolved.id } : null;
  }, [routeHandle, isAllRoute, listedCollection, collectionLookup]);

  // Old URL (edited handle, or the pre-handle "%20" name URL): move to the canonical one.
  const redirectHandle = collectionLookup?.redirect;
  useEffect(() => {
    if (!redirectHandle || redirectHandle === routeHandle) return;
    router.replace(
      {
        pathname: collectionPath({ handle: redirectHandle }),
        query: filtersToQuery(filtersFromQuery(router.query)),
      },
      undefined,
      { scroll: false },
    );
  }, [redirectHandle, routeHandle, router]);

  const productFilterKey = selectedCollection
    ? selectedCollection._id === "all"
      ? "all"
      : selectedCollection.handle || selectedCollection.title
    : undefined;

  // Price / stock / sort filters come from the URL and carry over when the collection changes.
  const { minPrice: qMin, maxPrice: qMax, inStock: qStock, sort: qSort } = router.query;
  const filters = useMemo(
    () => filtersFromQuery({ minPrice: qMin, maxPrice: qMax, inStock: qStock, sort: qSort }),
    [qMin, qMax, qStock, qSort],
  );
  const filtersOn = activeFilterCount(filters) > 0;

  const applyFilters = useCallback(
    (next) => {
      router.push(
        {
          pathname: router.pathname,
          query: { ...withoutFilters(router.query), ...filtersToQuery(next) },
        },
        undefined,
        { scroll: false },
      );
    },
    [router],
  );

  const {
    data: productData,
    isLoading: isProductsLoading,
    isFetchingNextPage,
    fetchNextPage,
    hasNextPage,
    error: productError,
  } = useStorefrontProducts(productFilterKey, filters);

  const collectionMissing =
    collectionLookupError?.code === "COLLECTION_NOT_FOUND" ||
    productError?.code === "COLLECTION_NOT_FOUND";
  const products = productData?.pages.flatMap((page) => page.products) || [];
  // Only the hidden listing filter reads this.
  // const priceRange = productData?.pages[0]?.priceRange || null;

  const hasHydrated = useCart((s) => s.hasHydrated);
  const setCart = useCart((s) => s.setCart);

  const cartProducts = useCart((s) => s.products);
  const cart = { products: cartProducts };

  const [showCart, setShowCart] = useState(false);
  const [showSideNav, setShowSideNav] = useState(false);

  const handleShowCart = useCallback(
    (boolVal = false) => {
      if (isCollectionsLoading || collectionsError || productError) return;
      setShowCart(boolVal);
    },
    [isCollectionsLoading, collectionsError, productError],
  );

  // The selection follows the URL; the query key changes with it, so no invalidate — that was
  // refetching the old collection AND the new one on every tap/untap.
  const setFilter = useCallback(
    (collection) => {
      router.push({
        pathname: pathForCollection(collection || ALL_PRODUCTS_OBJECT),
        query: filtersToQuery(filters),
      });
    },
    [router, filters],
  );

  useEffect(() => {
    if (inView && hasNextPage && !isFetchingNextPage) {
      fetchNextPage();
    }
  }, [inView, hasNextPage, isFetchingNextPage, fetchNextPage]);

  useEffect(() => {
    if (!products.length) return;
    const [lastElement] = products.slice(-1);
    setLastEl(lastElement.id);
  }, [products]);

  const updateAddedToCart = () => {};

  if (collectionMissing) {
    return <ShopStatusPage embedded message="The page you are looking for does not exist." />;
  }

  if (collectionsError || productError || collectionLookupError) {
    return <p className={`mt-8 px-5 ${statusCopyClassName}`}>Something went wrong.</p>;
  }

  return (
    <div className="scrollbar-none min-h-[calc(100vh-268px)] w-full font-montserrat">
      {hasHydrated && (
        <Basket
          isBasketOpen={showCart}
          setCart={setCart}
          cartData={cart}
          updateAddedToCart={updateAddedToCart}
          handleShowCart={handleShowCart}
          store={store}
          setRefresh={() => {}}
        />
      )}

      <NavBar
        cartActive={cart.products.length > 0}
        handleShowCart={handleShowCart}
        hideInAdvance={false}
        homeActive={Boolean(hasBanner)}
        store={store}
        theme={theme}
        storeName={store?.name || ""}
        toggleSideNav={() => setShowSideNav(!showSideNav)}
        sideNavOpen={showSideNav}
        homePage
      />

      <SideNavDrawer
        isOpen={showSideNav}
        toggleDrawer={() => setShowSideNav(!showSideNav)}
        handleShowCart={handleShowCart}
        store={store}
      />

      <div className="px-[22px]">
        <div className="relative z-10 mt-2 flex max-w-[500px] items-center justify-end gap-[10px]">
          {isCollectionsLoading || collections.length > 0 ? (
            <div className="hide-scrollbar flex min-w-0 flex-1 items-center gap-[10px] overflow-x-auto overflow-y-hidden scroll-smooth px-0 py-1">
              {collections.length > 0 ? (
                <StoreCollections
                  collections={collections}
                  setFilter={setFilter}
                  selectedCollection={selectedCollection}
                />
              ) : null}

              {isCollectionsLoading &&
                Array(3)
                  .fill(0)
                  .map((_, i) => (
                    <Skeleton
                      className="h-[42px] w-[100px] shrink-0 rounded-2xl border border-gray-200"
                      key={i}
                    />
                  ))}
            </div>
          ) : null}

          {/* Listing filter hidden for now.
          <ListingFilter filters={filters} priceRange={priceRange} onApply={applyFilters} /> */}
        </div>

        {products.length > 0 ? (
          <div className="relative mt-4 min-h-[80vh]">
            <RestorableList>
              {Array.from({ length: Math.ceil(products.length / 2) }, (_, rowIndex) => {
                const startIndex = rowIndex * 2;
                const rowProducts = products.slice(startIndex, startIndex + 2);

                return (
                  <div key={rowProducts[0].id} className="grid grid-cols-2 gap-4 pb-4">
                    {rowProducts.map((product) => (
                      <div
                        key={product.id}
                        className="min-w-0"
                        ref={lastEl === product.id ? ref : null}
                      >
                        <StoreItem product={product} top={rowIndex === 0} />
                      </div>
                    ))}
                  </div>
                );
              })}
            </RestorableList>
          </div>
        ) : (
          !isProductsLoading &&
          !collectionMissing &&
          (filtersOn ? (
            <div className="mt-6 flex flex-col items-start gap-3">
              <p className="text-base font-normal text-checkoutText">
                No products match these filters.
              </p>
              <button
                type="button"
                onClick={() => applyFilters({})}
                className="text-sm font-medium text-checkoutText underline"
              >
                Clear filters
              </button>
            </div>
          ) : (
            <p className="mt-6 text-base font-normal text-checkoutText">No products found.</p>
          ))
        )}

        {(isFetchingNextPage || isProductsLoading) && (
          <div className="mt-4 grid grid-cols-2 gap-4">
            {Array(6)
              .fill(0)
              .map((_, i) => (
                <div key={i}>
                  <SkeletonCard
                    imageClass="aspect-square h-auto w-full rounded-lg"
                    titleClass="h-4 w-full"
                    descriptionClass="h-4 w-2/3"
                    containerClass="w-full"
                    key={i}
                  />
                </div>
              ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default CollectionHome;
